import React from "react";
import { useNavigate } from "react-router-dom";
import { BookOpen, Beaker, Globe, Music, Palette, Calculator, ArrowRight, Trophy, Brain, Target, Cpu, Megaphone } from "lucide-react";
import { Button } from "../ui/button";

const btn3D = "shadow-[0_6px_0_rgba(0,0,0,0.12),0_8px_16px_rgba(0,0,0,0.08)] hover:shadow-[0_4px_0_rgba(0,0,0,0.12),0_6px_12px_rgba(0,0,0,0.06)] hover:translate-y-[2px] active:shadow-[0_1px_0_rgba(0,0,0,0.12),0_2px_4px_rgba(0,0,0,0.04)] active:translate-y-[5px] transition-all duration-150 ease-out";

export const AcademicsShowcase = () => {
  const navigate = useNavigate();

  const programs = [
    {
      icon: BookOpen,
      title: "Primary Education",
      description: "Building strong foundations in literacy, numeracy, and critical thinking.",
      image: "https://customer-assets.emergentagent.com/job_248403c7-c2c6-44b2-9d63-d9aa2b4e99b6/artifacts/lyvaydxl_WhatsApp%20Image%202026-01-20%20at%206.40.13%20AM%20%281%29.jpeg",
      color: "bg-blue-500"
    },
    {
      icon: Beaker,
      title: "Science & Technology",
      description: "Hands-on experiments and modern tech education for curious minds.",
      image: "https://images.unsplash.com/photo-1771408427146-09be9a1d4535?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwyfHxzdHVkZW50cyUyMGNvbXB1dGVyJTIwdGVjaG5vbG9neSUyMGNsYXNzcm9vbXxlbnwwfHx8fDE3NzIxMTY1Mjh8MA&ixlib=rb-4.1.0&q=85",
      color: "bg-green-500"
    },
    {
      icon: Globe,
      title: "Languages & Social Studies",
      description: "Developing global citizens with cultural awareness and communication skills.",
      image: "https://customer-assets.emergentagent.com/job_248403c7-c2c6-44b2-9d63-d9aa2b4e99b6/artifacts/ikohlqeh_WhatsApp%20Image%202026-01-20%20at%206.48.57%20AM%20%282%29.jpeg",
      color: "bg-purple-500"
    },
    {
      icon: Music,
      title: "Arts & Music",
      description: "Nurturing creativity through visual arts, music, and performing arts.",
      image: "https://images.unsplash.com/photo-1769097542113-49f995910fc5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwxfHxjaGlsZCUyMHBsYXlpbmclMjBwaWFubyUyMG11c2ljfGVufDB8fHx8MTc3MjExNjQyNXww&ixlib=rb-4.1.0&q=85",
      color: "bg-pink-500"
    }
  ];

  return (
    <section className="py-16 bg-white" data-testid="academics-showcase">
      <div className="max-w-7xl mx-auto px-4">
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
          <div>
            <div className="inline-flex items-center gap-2 bg-[#002147]/10 text-[#002147] px-4 py-2 rounded-full mb-4">
              <BookOpen size={18} />
              <span className="font-semibold text-sm">Academic Excellence</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-[#002147] mb-4">
              Our Academic Programs
            </h2>
            <p className="text-gray-600 max-w-xl">
              Comprehensive curriculum designed to prepare students for success in higher education and beyond.
            </p>
          </div>
          <Button 
            className="mt-6 md:mt-0 bg-[#FF6B00] hover:bg-[#E65C00] text-white"
            onClick={() => navigate("/course-catalog")}
            data-testid="view-all-courses-btn"
          >
            View All Courses <ArrowRight className="ml-2" size={18} />
          </Button>
        </div>

        {/* Programs Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {programs.map((program, index) => (
            <div 
              key={index}
              className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100"
              data-testid={`program-card-${index}`}
            >
              <div className="relative h-48 overflow-hidden">
                <img
                  src={program.image}
                  alt={program.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className={`absolute top-4 left-4 ${program.color} p-3 rounded-xl shadow-lg`}>
                  <program.icon className="w-6 h-6 text-white" />
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-[#002147] mb-2 group-hover:text-[#FF6B00] transition-colors">
                  {program.title}
                </h3>
                <p className="text-gray-600 text-sm">
                  {program.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Feature Banner */}
        <div className="mt-12 relative rounded-2xl overflow-hidden">
          <img
            src="https://customer-assets.emergentagent.com/job_248403c7-c2c6-44b2-9d63-d9aa2b4e99b6/artifacts/lkjapsio_WhatsApp%20Image%202026-01-20%20at%206.40.16%20AM%20%281%29.jpeg"
            alt="Students in classroom"
            className="w-full h-64 md:h-80 object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#002147]/90 to-[#002147]/40" />
          <div className="absolute inset-0 flex items-center">
            <div className="px-8 md:px-12 max-w-xl">
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                Beyond the Classroom
              </h3>
              <p className="text-gray-200 mb-6">
                We believe in holistic education. Our students excel in sports, arts, and extracurricular activities that build character and leadership skills.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AcademicsShowcase;
